import React, {
  createContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import type { ReactNode } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { getCookie } from "@repo/core";

// WebSocket connection states (mapped from ReadyState)
type WebSocketState = "connecting" | "connected" | "disconnected" | "error";

// Message type for WebSocket communications
export interface WebSocketMessage {
  type:
    | "MESSAGE"
    | "REFRESH"
    | "PREPARED"
    | "info"
    | "warning"
    | "error"
    | "success"
    | "notification"
    | "text"
    | string;
  target?: string;
  uuid?: string;
  data?: unknown;
  timestamp?: number;
  severity?: "info" | "warning" | "error";
  title?: string;
  message?: string;
}

// WebSocket context type
interface WebSocketContextType {
  // Connection state
  state: WebSocketState;
  isConnected: boolean;

  // Message handling
  appliedMessages: Set<string>;
  addMessage: (messageId: string) => void;
  removeMessage: (messageId: string) => void;

  // Model management
  obsoleteModels: Set<string>;
  addObsoleteModel: (modelId: string) => void;
  removeObsoleteModel: (modelId: string) => void;

  // Reasoning requests awaiting a "dataPrepared" broadcast, keyed by
  // "{userId}#{strategyId}#{period}#{modelId}" (matches the broadcast target's composite key)
  pendingReasoningRequests: Set<string>;
  addPendingReasoningRequest: (key: string) => void;
  removePendingReasoningRequest: (key: string) => void;

  // WebSocket functions
  sendMessage: (message: WebSocketMessage) => void;
  connect: (url?: string) => void;
  disconnect: () => void;

  // Last received message. Kept for convenience, but it is a single state slot:
  // React may coalesce two frames that arrive in the same task into one render,
  // so anything that must observe EVERY message has to use subscribeToMessages.
  lastMessage: WebSocketMessage | null;

  // Subscribe to every parsed inbound message. The listener is invoked
  // synchronously from the socket's onmessage handler, so no message is dropped.
  // Returns an unsubscribe function.
  subscribeToMessages: (
    listener: (message: WebSocketMessage) => void
  ) => () => void;
}

// Create the WebSocket context
export const WebSocketContext = createContext<WebSocketContextType | null>(
  null
);

// Provider props
interface WebSocketProviderProps {
  children: ReactNode;
  url?: string;
  autoConnect?: boolean;
}

// WebSocket Provider component
export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
  url,
  autoConnect = false,
}) => {
  const [obsoleteModels, setObsoleteModels] = useState<Set<string>>(new Set());
  const [pendingReasoningRequests, setPendingReasoningRequests] = useState<Set<string>>(new Set());
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [shouldConnect, setShouldConnect] = useState(autoConnect);
  const appliedMessagesRef = useRef<Set<string>>(new Set());
  const pingIntervalRef = useRef<number | null>(null);
  const messageListenersRef = useRef<Set<(message: WebSocketMessage) => void>>(
    new Set()
  );

  const subscribeToMessages = useCallback(
    (listener: (message: WebSocketMessage) => void) => {
      messageListenersRef.current.add(listener);
      return () => {
        messageListenersRef.current.delete(listener);
      };
    },
    []
  );

  // Parses a raw frame and fans it out to every subscriber. Called straight from
  // the socket's onmessage handler (see the onMessage option below) rather than
  // from an effect on `lastMessage`, so bursts of frames cannot be collapsed by
  // React's automatic batching.
  const handleRawMessage = useCallback((event: MessageEvent) => {
    try {
      console.log("📨 Raw WebSocket message received:", event.data);

      // Handle both JSON and plain text messages
      let message: WebSocketMessage;

      try {
        message = JSON.parse(event.data);
        message.timestamp = Date.now();

        // Generate UUID if not present
        if (!message.uuid) {
          message.uuid = `generated-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`;
        }
      } catch {
        // Handle plain text messages (like PONG responses)
        message = {
          type: "text",
          data: event.data,
          timestamp: Date.now(),
          uuid: `text-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        };
      }

      setLastMessage(message);
      messageListenersRef.current.forEach((listener) => {
        try {
          listener(message);
        } catch (error) {
          console.error("WebSocket message listener failed:", error);
        }
      });
    } catch (error) {
      console.error("Failed to process WebSocket message:", error);
    }
  }, []);

  // Build WebSocket URL with authentication
  const buildWebSocketUrl = useCallback((): string => {
    const baseUrl =
      import.meta.env.VITE_WS_ENDPOINT ||
      import.meta.env.REACT_APP_WS_ENDPOINT ||
      url ||
      "ws://localhost:8080";
    const accessToken = getCookie("accessToken");

    if (accessToken && baseUrl.includes("amazonaws.com")) {
      // AWS WebSocket endpoint with token authentication
      return `${baseUrl}?token=${accessToken}`;
    }

    return baseUrl;
  }, [url]);

  const wsUrl = buildWebSocketUrl();

  // Use react-use-websocket hook with shouldConnect state control
  const {
    sendMessage: sendRawMessage,
    readyState,
    getWebSocket,
  } = useWebSocket(shouldConnect ? wsUrl : null, {
    onMessage: handleRawMessage,
    shouldReconnect: () => shouldConnect,
    share: true,
    retryOnError: true,
    reconnectAttempts: 10,
    reconnectInterval: 5000,
    onOpen: () => {
      console.log(
        "🔌 WebSocket connected successfully - State:",
        ReadyState.OPEN
      );

      // Start PING interval (every 60 seconds)
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }

      pingIntervalRef.current = setInterval(() => {
        console.log("📡 Sending PING message");
        sendRawMessage(
          JSON.stringify({
            action: "sendMessage",
            data: {
              type: "PING",
            },
          })
        );
      }, 60000); // 60 seconds
    },
    onClose: () => {
      console.log("🔌 WebSocket disconnected - State:", readyState);

      // Clear PING interval on disconnect
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    },
    onError: (error) => {
      console.error("WebSocket error:", error);
    },
  });

  // Convert ReadyState to our WebSocketState
  const getState = (): WebSocketState => {
    switch (readyState) {
      case ReadyState.CONNECTING:
        return "connecting";
      case ReadyState.OPEN:
        return "connected";
      case ReadyState.CLOSING:
      case ReadyState.CLOSED:
        return "disconnected";
      default:
        return "error";
    }
  };

  const state = getState();
  const isConnected = state === "connected";

  // Message management functions
  const addMessage = (messageId: string) => {
    appliedMessagesRef.current.add(messageId);
  };

  const removeMessage = (messageId: string) => {
    appliedMessagesRef.current.delete(messageId);
  };

  // Model management functions
  const addObsoleteModel = (modelId: string) => {
    setObsoleteModels((prev) => {
      const next = new Set(prev);
      next.add(modelId);
      return next;
    });
  };

  const removeObsoleteModel = (modelId: string) => {
    setObsoleteModels((prev) => {
      const next = new Set(prev);
      next.delete(modelId);
      return next;
    });
  };

  // Pending reasoning request tracking functions
  const addPendingReasoningRequest = (key: string) => {
    setPendingReasoningRequests((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const removePendingReasoningRequest = (key: string) => {
    setPendingReasoningRequests((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  // Connect to WebSocket (manual connection)
  const connect = useCallback(
    (newUrl?: string) => {
      // Check if already connecting or connected to prevent duplicate connections
      if (
        readyState === ReadyState.CONNECTING ||
        readyState === ReadyState.OPEN
      ) {
        console.log(
          `🔌 WebSocket already ${
            readyState === ReadyState.CONNECTING ? "connecting" : "connected"
          }, skipping duplicate connection attempt...`
        );
        return;
      }

      if (newUrl) {
        console.log("🔌 Connecting to WebSocket with new URL:", newUrl);
        // For URL changes, we'd need to update the hook URL, which is complex
        // For now, log the attempt
      } else {
        console.log("🔌 Attempting to connect to WebSocket");
      }

      setShouldConnect(true);
    },
    [readyState]
  );

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    console.log("🔌 Manually disconnecting WebSocket");
    setShouldConnect(false);

    // Clear PING interval on manual disconnect
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    const ws = getWebSocket();
    if (ws) {
      ws.close(1000, "Manual disconnect");
    }
  }, [getWebSocket]);

  // Send message through WebSocket
  const sendMessage = (message: WebSocketMessage) => {
    if (readyState === ReadyState.OPEN) {
      try {
        const messageToSend = {
          ...message,
          timestamp: Date.now(),
        };
        sendRawMessage(JSON.stringify(messageToSend));
        console.log("WebSocket message sent:", messageToSend);
      } catch (error) {
        console.error("Failed to send WebSocket message:", error);
      }
    } else {
      console.warn("WebSocket is not connected. Cannot send message:", message);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };
  }, []);

  // Handle autoConnect initialization
  useEffect(() => {
    setShouldConnect(autoConnect);
  }, [autoConnect]);

  const value: WebSocketContextType = {
    state,
    isConnected,
    appliedMessages: appliedMessagesRef.current,
    addMessage,
    removeMessage,
    obsoleteModels,
    addObsoleteModel,
    removeObsoleteModel,
    pendingReasoningRequests,
    addPendingReasoningRequest,
    removePendingReasoningRequest,
    sendMessage,
    connect,
    disconnect,
    lastMessage,
    subscribeToMessages,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketProvider;
